import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, List, RefreshCw, Folder, FileText, Image } from '../../utils/icons';
import PageHeader from '../shared/PageHeader';
import DriveBreadcrumbs from '../../components/LegalForms/DriveBreadcrumbs';
import DriveItemCard from '../../components/LegalForms/DriveItemCard';
import { useFolderContents } from '../../hooks/useDrive';
import { getFileTypeLabel, formatFileSize, formatModifiedDate, getMimeTypeIcon } from '../../utils/drive-helpers';

const GridIcon = Grid as React.ComponentType<{ className?: string; size?: number }>;
const ListIcon = List as React.ComponentType<{ className?: string; size?: number }>;
const RefreshIcon = RefreshCw as React.ComponentType<{ className?: string; size?: number }>;
const FolderIcon = Folder as React.ComponentType<{ className?: string; size?: number }>;
const FileTextIcon = FileText as React.ComponentType<{ className?: string; size?: number }>;
const ImageIcon = Image as React.ComponentType<{ className?: string; size?: number }>;

const VIEW_STORAGE_KEY = 'clicklaw.legalFormsView';

const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  folder: FolderIcon,
  pdf: FileTextIcon,
  image: ImageIcon,
  file: FileTextIcon,
};

const LegalFormsBrowserPage: React.FC = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const effectiveFolderId = folderId || 'root';

  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'list' ? 'list' : 'grid';
  });

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const { data, isLoading, isError, error, refetch, isFetching } = useFolderContents(effectiveFolderId);

  const items = data?.items ?? [];
  const breadcrumbs = data?.breadcrumbs ?? [];

  // Sort: folders first (alphabetical), then files (alphabetical)
  const sortedItems = [...items].sort((a, b) => {
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleItemClick = (item: typeof items[0]) => {
    if (item.isFolder) {
      navigate(`/dashboard/legal-forms/folder/${item.id}`);
    } else {
      navigate(`/dashboard/legal-forms/file/${item.id}`);
    }
  };

  return (
    <div>
      <PageHeader title="Forms and Precedents" subtitle="Browse and download legal forms." />

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <DriveBreadcrumbs breadcrumbs={breadcrumbs} />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:text-slate-300 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshIcon size={16} className={isFetching ? 'animate-spin' : ''} />
            </button>
            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-slate-600 text-slate-700 dark:text-slate-200 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
                title="Grid view"
              >
                <GridIcon size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-slate-600 text-slate-700 dark:text-slate-200 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
                title="List view"
              >
                <ListIcon size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
            : 'space-y-2'
          }>
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-gray-100 dark:bg-slate-700/50 rounded-2xl animate-pulse"
                style={{ height: viewMode === 'grid' ? 120 : 52 }}
              />
            ))}
          </div>
        )}

        {/* Error */}
        {isError && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
              {(error as any)?.message || 'Failed to load folder contents.'}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              <RefreshIcon size={14} />
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && sortedItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FolderIcon className="text-slate-300 dark:text-slate-600 mb-3" size={48} />
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              This folder is empty
            </p>
          </div>
        )}

        {/* Grid view */}
        {!isLoading && !isError && sortedItems.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {sortedItems.map((item) => (
              <DriveItemCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* List view */}
        {!isLoading && !isError && sortedItems.length > 0 && viewMode === 'list' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-slate-700">
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden md:table-cell">Modified</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden md:table-cell">Size</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => {
                  const iconType = getMimeTypeIcon(item.mimeType);
                  const Icon = iconMap[iconType];
                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="border-b border-gray-50 dark:border-slate-700/50 hover:bg-gray-50 dark:hover:bg-slate-700/30 cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2.5">
                          <Icon
                            size={18}
                            className={
                              item.isFolder
                                ? 'text-amber-500 flex-shrink-0'
                                : iconType === 'image'
                                  ? 'text-emerald-500 flex-shrink-0'
                                  : 'text-blue-500 flex-shrink-0'
                            }
                          />
                          <span className="text-slate-700 dark:text-slate-200 truncate max-w-[300px]">
                            {item.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 dark:text-slate-500 hidden sm:table-cell">
                        {getFileTypeLabel(item.mimeType)}
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 dark:text-slate-500 hidden md:table-cell">
                        {formatModifiedDate(item.modifiedTime)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-400 dark:text-slate-500 hidden md:table-cell">
                        {item.isFolder ? '—' : formatFileSize(item.size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalFormsBrowserPage;
